import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { useTheme } from '../context/ThemeContext';

const MarketingAnalytics = () => {
  const { isDark } = useTheme();
  const [predictiveModels, setPredictiveModels] = useState([]);
  const [predictions, setPredictions] = useState([]);
  const [abTests, setAbTests] = useState([]);
  const [leads, setLeads] = useState([]);
  const [customerJourneys, setCustomerJourneys] = useState([]);
  const [activeTab, setActiveTab] = useState('predictive');
  const [selectedModel, setSelectedModel] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      switch (activeTab) {
        case 'predictive':
          const modelsResponse = await api.get('/marketing/analytics/models');
          setPredictiveModels(modelsResponse.data.models);
          break;
        case 'testing':
          const testsResponse = await api.get('/marketing/testing/ab-tests');
          setAbTests(testsResponse.data.tests);
          break;
        case 'leads':
          const leadsResponse = await api.get('/marketing/leads');
          setLeads(leadsResponse.data.leads);
          break;
        case 'journeys':
          const journeysResponse = await api.get('/marketing/analytics/customer-journeys');
          setCustomerJourneys(journeysResponse.data.journeys);
          break;
      }
    } catch (error) {
      console.error('Error fetching analytics data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchModelPredictions = async (modelId) => {
    try {
      const response = await api.get(`/marketing/analytics/predictions/${modelId}`);
      setPredictions(response.data.predictions);
    } catch (error) {
      console.error('Error fetching predictions:', error);
    }
  };

  const getModelTypeIcon = (type) => {
    const icons = {
      churn_prediction: '📉',
      lifetime_value: '💰',
      lead_scoring: '🎯',
      conversion_rate: '📊',
      price_optimization: '💎',
      campaign_performance: '📈',
      customer_segmentation: '👥',
      retention_analysis: '🔄'
    };
    return icons[type] || '🤖';
  };

  const getTestStatusColor = (status) => {
    const colors = {
      draft: '#6b7280',
      running: '#f59e0b',
      completed: '#10b981',
      paused: '#ef4444'
    };
    return colors[status] || '#6b7280';
  };

  const getLeadGradeColor = (grade) => {
    const colors = {
      A: '#10b981',
      B: '#f59e0b',
      C: '#ef4444',
      D: '#6b7280'
    };
    return colors[grade] || '#6b7280';
  };

  const renderPredictiveAnalytics = () => (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
      {/* Models List */}
      <div>
        <h2 style={{ color: isDark ? '#f9fafb' : 'inherit' }}>Predictive Models</h2>
        <div style={{ display: 'grid', gap: '12px' }}>
          {predictiveModels.map(model => (
            <div
              key={model.id}
              onClick={() => {
                setSelectedModel(model);
                fetchModelPredictions(model.id);
              }}
              style={{
                border: selectedModel?.id === model.id ? '2px solid var(--primary-600)' : `1px solid ${isDark ? '#4b5563' : '#e5e7eb'}`,
                borderRadius: '8px',
                padding: '16px',
                cursor: 'pointer',
                backgroundColor: model.is_active ? (isDark ? '#1f2937' : '#ffffff') : (isDark ? '#111827' : '#f9fafb')
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                <span style={{ fontSize: '24px' }}>
                  {getModelTypeIcon(model.model_type)}
                </span>
                <div style={{ flex: 1 }}>
                  <h3 style={{ margin: 0, fontSize: '16px', color: isDark ? '#f9fafb' : 'inherit' }}>{model.name}</h3>
                  <div style={{ fontSize: '14px', color: isDark ? '#9ca3af' : '#6b7280' }}>
                    {model.model_type.replace('_', ' ').toUpperCase()}
                  </div>
                </div>
                <div style={{
                  backgroundColor: model.is_production ? '#10b981' : '#f59e0b',
                  color: 'white',
                  padding: '4px 8px',
                  borderRadius: '8px',
                  fontSize: '12px'
                }}>
                  {model.is_production ? 'PROD' : 'DEV'}
                </div>
              </div>

              <p style={{ margin: '0 0 12px 0', fontSize: '14px', color: isDark ? '#d1d5db' : '#374151' }}>
                {model.description}
              </p>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#10b981' }}>
                    {Math.round(model.accuracy * 100)}%
                  </div>
                  <div style={{ fontSize: '12px', color: isDark ? '#9ca3af' : '#6b7280' }}>Accuracy</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '16px', fontWeight: 'bold', color: 'var(--primary-600)' }}>
                    {Math.round(model.precision * 100)}%
                  </div>
                  <div style={{ fontSize: '12px', color: isDark ? '#9ca3af' : '#6b7280' }}>Precision</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#f59e0b' }}>
                    {Math.round(model.recall * 100)}%
                  </div>
                  <div style={{ fontSize: '12px', color: isDark ? '#9ca3af' : '#6b7280' }}>Recall</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#ef4444' }}>
                    {Math.round(model.f1_score * 100)}%
                  </div>
                  <div style={{ fontSize: '12px', color: isDark ? '#9ca3af' : '#6b7280' }}>F1 Score</div>
                </div>
              </div>

              <div style={{ marginTop: '8px', fontSize: '12px', color: isDark ? '#9ca3af' : '#6b7280' }}>
                Training Data: {model.training_data_size?.toLocaleString()} records
                {model.last_trained && (
                  <span> | Last Trained: {new Date(model.last_trained).toLocaleDateString()}</span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Predictions */}
      <div>
        {selectedModel ? (
          <div>
            <h2 style={{ color: isDark ? '#f9fafb' : 'inherit' }}>{selectedModel.name} - Recent Predictions</h2>
            <div style={{ display: 'grid', gap: '12px' }}>
              {predictions.map(prediction => (
                <div
                  key={prediction.id}
                  style={{
                    border: `1px solid ${isDark ? '#4b5563' : '#e5e7eb'}`,
                    borderRadius: '8px',
                    padding: '16px',
                    backgroundColor: isDark ? '#1f2937' : '#ffffff'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'between', alignItems: 'center', marginBottom: '8px' }}>
                    <div>
                      <h4 style={{ margin: '0 0 4px 0', fontSize: '14px', color: isDark ? '#f9fafb' : 'inherit' }}>
                        {prediction.prediction_type.replace('_', ' ').toUpperCase()}
                      </h4>
                      <div style={{ fontSize: '12px', color: isDark ? '#9ca3af' : '#6b7280' }}>
                        {new Date(prediction.prediction_date).toLocaleDateString()}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: '18px', fontWeight: 'bold', color: 'var(--primary-600)' }}>
                        {prediction.prediction_value}
                      </div>
                      <div style={{ fontSize: '12px', color: isDark ? '#9ca3af' : '#6b7280' }}>
                        {Math.round(prediction.confidence_score * 100)}% confidence
                      </div>
                    </div>
                  </div>

                  {prediction.actual_value !== null && (
                    <div style={{ marginTop: '8px', padding: '8px', backgroundColor: isDark ? '#374151' : '#f3f4f6', borderRadius: '8px' }}>
                      <div style={{ display: 'flex', justifyContent: 'between' }}>
                        <span style={{ fontSize: '12px', color: isDark ? '#e5e7eb' : 'inherit' }}>Actual: {prediction.actual_value}</span>
                        <span style={{ 
                          fontSize: '12px',
                          color: prediction.prediction_accuracy > 0.8 ? '#10b981' : '#ef4444'
                        }}>
                          Accuracy: {Math.round(prediction.prediction_accuracy * 100)}%
                        </span>
                      </div>
                    </div>
                  )}

                  {prediction.expires_at && (
                    <div style={{ fontSize: '12px', color: isDark ? '#9ca3af' : '#6b7280', marginTop: '8px' }}>
                      Expires: {new Date(prediction.expires_at).toLocaleDateString()}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '40px', color: isDark ? '#9ca3af' : '#6b7280' }}>
            Select a model to view predictions
          </div>
        )}
      </div>
    </div>
  );

  const renderABTests = () => (
    <div style={{ display: 'grid', gap: '16px' }}>
      {abTests.map(test => (
        <div
          key={test.id}
          style={{
            border: `1px solid ${isDark ? '#4b5563' : '#e5e7eb'}`,
            borderRadius: '8px',
            padding: '20px',
            backgroundColor: isDark ? '#1f2937' : '#ffffff'
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'between', alignItems: 'flex-start', marginBottom: '12px' }}>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <h3 style={{ margin: 0, fontSize: '18px', color: isDark ? '#f9fafb' : 'inherit' }}>{test.name}</h3>
                <div style={{
                  backgroundColor: getTestStatusColor(test.status),
                  color: 'white',
                  padding: '4px 8px',
                  borderRadius: '8px',
                  fontSize: '12px'
                }}>
                  {test.status.toUpperCase()}
                </div>
                {test.winner_variant && (
                  <div style={{
                    backgroundColor: '#10b981',
                    color: 'white',
                    padding: '4px 8px',
                    borderRadius: '8px',
                    fontSize: '12px'
                  }}>
                    Winner: {test.winner_variant}
                  </div>
                )}
              </div>
              <div style={{ fontSize: '14px', color: isDark ? '#9ca3af' : '#6b7280', marginTop: '4px' }}>
                {test.test_type.replace('_', ' ').toUpperCase()}
              </div>
            </div>
            {test.statistical_significance && (
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: '18px', fontWeight: 'bold', color: 'var(--primary-600)' }}>
                  {Math.round(test.statistical_significance * 100)}%
                </div>
                <div style={{ fontSize: '12px', color: isDark ? '#9ca3af' : '#6b7280' }}>Significance</div>
              </div>
            )}
          </div>

          <p style={{ margin: '0 0 12px 0', fontSize: '14px', color: isDark ? '#d1d5db' : '#374151' }}>
            {test.description}
          </p>

          {test.hypothesis && (
            <div style={{ marginBottom: '12px' }}>
              <strong style={{ fontSize: '14px', color: isDark ? '#e5e7eb' : 'inherit' }}>Hypothesis:</strong>
              <div style={{ 
                backgroundColor: isDark ? '#374151' : '#f3f4f6', 
                padding: '8px', 
                borderRadius: '8px',
                fontSize: '14px',
                marginTop: '4px',
                color: isDark ? '#e5e7eb' : 'inherit'
              }}>
                {test.hypothesis}
              </div>
            </div>
          )}

          <div style={{ display: 'flex', gap: '16px', fontSize: '12px', color: isDark ? '#9ca3af' : '#6b7280' }}>
            <div>
              <strong>Success Metric:</strong> {test.success_metric}
            </div>
            {test.started_at && (
              <div>
                <strong>Started:</strong> {new Date(test.started_at).toLocaleDateString()}
              </div>
            )}
            {test.ended_at && (
              <div>
                <strong>Ended:</strong> {new Date(test.ended_at).toLocaleDateString()}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );

  const renderLeads = () => (
    <div style={{ display: 'grid', gap: '12px' }}>
      {leads.map(lead => (
        <div
          key={lead.id}
          style={{
            border: `1px solid ${isDark ? '#4b5563' : '#e5e7eb'}`,
            borderRadius: '8px',
            padding: '16px',
            backgroundColor: isDark ? '#1f2937' : '#ffffff'
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'between', alignItems: 'flex-start' }}>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: '16px', color: isDark ? '#f9fafb' : 'inherit' }}>
                    {lead.first_name} {lead.last_name}
                  </h3>
                  <div style={{ fontSize: '14px', color: isDark ? '#9ca3af' : '#6b7280' }}>
                    {lead.email} | {lead.company}
                  </div>
                </div>
                <div style={{
                  backgroundColor: getLeadGradeColor(lead.lead_grade),
                  color: 'white',
                  padding: '4px 8px',
                  borderRadius: '8px',
                  fontSize: '12px'
                }}>
                  Grade {lead.lead_grade}
                </div>
                <div style={{
                  backgroundColor: lead.converted ? '#10b981' : '#6b7280',
                  color: 'white',
                  padding: '4px 8px',
                  borderRadius: '8px',
                  fontSize: '12px'
                }}>
                  {lead.converted ? 'CONVERTED' : lead.qualification_status.toUpperCase()}
                </div>
              </div>

              <div style={{ display: 'flex', gap: '16px', fontSize: '12px', color: isDark ? '#9ca3af' : '#6b7280' }}>
                <div><strong>Job Title:</strong> {lead.job_title}</div>
                <div><strong>Source:</strong> {lead.source}</div>
                <div><strong>Stage:</strong> {lead.stage}</div>
                {lead.last_contacted && (
                  <div><strong>Last Contact:</strong> {new Date(lead.last_contacted).toLocaleDateString()}</div>
                )}
              </div>
            </div>

            <div style={{ textAlign: 'right', minWidth: '200px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px', marginBottom: '8px' }}>
                <div>
                  <div style={{ fontSize: '16px', fontWeight: 'bold', color: 'var(--primary-600)' }}>
                    {lead.lead_score}
                  </div>
                  <div style={{ fontSize: '11px', color: isDark ? '#9ca3af' : '#6b7280' }}>Lead Score</div>
                </div>
                <div>
                  <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#10b981' }}>
                    {lead.email_opens}
                  </div>
                  <div style={{ fontSize: '11px', color: isDark ? '#9ca3af' : '#6b7280' }}>Email Opens</div>
                </div>
                <div>
                  <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#f59e0b' }}>
                    {lead.email_clicks}
                  </div>
                  <div style={{ fontSize: '11px', color: isDark ? '#9ca3af' : '#6b7280' }}>Email Clicks</div>
                </div>
                <div>
                  <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#ef4444' }}>
                    {lead.website_visits}
                  </div>
                  <div style={{ fontSize: '11px', color: isDark ? '#9ca3af' : '#6b7280' }}>Site Visits</div>
                </div>
              </div>

              {lead.converted && lead.conversion_value && (
                <div style={{ 
                  backgroundColor: isDark ? '#065f46' : '#d1fae5',
                  color: isDark ? '#a7f3d0' : '#065f46',
                  padding: '8px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: 'bold'
                }}>
                  Value: ${lead.conversion_value.toLocaleString()}
                </div>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  const renderCustomerJourneys = () => (
    <div style={{ display: 'grid', gap: '16px' }}>
      {customerJourneys.map(journey => (
        <div
          key={journey.id}
          style={{
            border: `1px solid ${isDark ? '#4b5563' : '#e5e7eb'}`,
            borderRadius: '8px',
            padding: '20px',
            backgroundColor: isDark ? '#1f2937' : '#ffffff'
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'between', alignItems: 'flex-start', marginBottom: '12px' }}>
            <div>
              <h3 style={{ margin: 0, fontSize: '18px', color: isDark ? '#f9fafb' : 'inherit' }}>Customer Journey #{journey.id}</h3>
              <div style={{ fontSize: '14px', color: isDark ? '#9ca3af' : '#6b7280', marginTop: '4px' }}>
                Stage: {journey.stage} | Duration: {journey.journey_duration} days
              </div>
            </div>
            <div style={{
              backgroundColor: journey.converted ? '#10b981' : '#6b7280',
              color: 'white',
              padding: '4px 8px',
              borderRadius: '8px',
              fontSize: '12px'
            }}>
              {journey.converted ? 'CONVERTED' : 'ACTIVE'}
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '12px' }}>
            <div>
              <strong style={{ fontSize: '14px', color: isDark ? '#e5e7eb' : 'inherit' }}>First Touch:</strong>
              <div style={{ fontSize: '14px', color: isDark ? '#9ca3af' : '#6b7280', marginTop: '4px' }}>
                {journey.first_touch_source}
                {journey.first_touch_campaign && ` - ${journey.first_touch_campaign}`}
              </div>
            </div>
            <div>
              <strong style={{ fontSize: '14px', color: isDark ? '#e5e7eb' : 'inherit' }}>Last Touch:</strong>
              <div style={{ fontSize: '14px', color: isDark ? '#9ca3af' : '#6b7280', marginTop: '4px' }}>
                {journey.last_touch_source}
                {journey.last_touch_campaign && ` - ${journey.last_touch_campaign}`}
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '16px', fontSize: '12px', color: isDark ? '#9ca3af' : '#6b7280' }}>
            <div><strong>Touchpoints:</strong> {journey.touchpoint_count}</div>
            {journey.converted && (
              <>
                <div><strong>Conversion Date:</strong> {new Date(journey.conversion_date).toLocaleDateString()}</div>
                <div><strong>Value:</strong> ${journey.conversion_value?.toLocaleString() || '0'}</div>
              </>
            )}
          </div>
        </div>
      ))}
    </div>
  );

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '400px' 
      }}>
        Loading analytics...
      </div>
    );
  }

  return (
    <div>
      {/* Breadcrumb Navigation */}
      <nav className="breadcrumb">
        <button
          onClick={() => window.location.href = '/'}
          className="breadcrumb-link"
        >
          Dashboard
        </button>
        <span className="breadcrumb-separator">/</span>
        <span className="breadcrumb-current">
          Marketing Analytics
        </span>
      </nav>

      {/* Main Card */}
      <div className="card">
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem',
          paddingBottom: '1.5rem',
          borderBottom: '3px solid #EF4444'
        }}>
          <div>
            <h2 style={{
              color: 'var(--gray-900)',
              fontSize: '1.5rem',
              fontWeight: 'bold',
              margin: '0 0 0.25rem 0'
            }}>
              Marketing Analytics
            </h2>
            <p style={{
              color: 'var(--gray-600)',
              margin: 0,
              fontSize: '0.875rem'
            }}>
              Advanced analytics, predictions, and customer insights
            </p>
          </div>
        </div>

        {/* Tab Navigation */}
        <div style={{ marginBottom: '1.5rem' }}>
          <div style={{
            display: 'flex',
            borderBottom: '2px solid var(--gray-200)',
            marginBottom: '0',
            gap: 'var(--space-1)'
          }}>
          {[
            { key: 'predictive', label: 'Predictive Models', icon: '🤖', count: predictiveModels.length },
            { key: 'testing', label: 'A/B Testing', icon: '🧪', count: abTests.length },
            { key: 'leads', label: 'Lead Analytics', icon: '🎯', count: leads.length },
            { key: 'journeys', label: 'Customer Journeys', icon: '🗺️', count: customerJourneys.length }
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              style={{
                padding: 'var(--space-3) var(--space-4)',
                background: 'none',
                border: 'none',
                borderBottom: `3px solid ${activeTab === tab.key ? 'var(--primary-600)' : 'transparent'}`,
                cursor: 'pointer',
                fontWeight: '600',
                color: activeTab === tab.key ? 'var(--primary-600)' : 'var(--gray-500)',
                transition: 'all 0.3s',
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-2)'
              }}
            >
              {tab.icon} {tab.label} ({tab.count})
            </button>
          ))}
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'predictive' && renderPredictiveAnalytics()}
        {activeTab === 'testing' && renderABTests()}
        {activeTab === 'leads' && renderLeads()}
        {activeTab === 'journeys' && renderCustomerJourneys()}
      </div>
    </div>
  );
};

export default MarketingAnalytics;